import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Col, Form, Input, InputNumber, message, Modal, Popconfirm,
  Radio, Row, Select, Space, Switch, Table, Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../api/client';
import useAuthStore from '../store/authStore';
import {
  Vehicle, VehiclePayload, OwnerType,
  listVehicles, createVehicle, updateVehicle, archiveVehicle,
} from '../api/vehicles';
import VehicleInfoDrawer from '../components/VehicleInfoDrawer';
import CounterpartyInfoDrawer from '../components/CounterpartyInfoDrawer';
import IndividualInfoDrawer from '../components/IndividualInfoDrawer';

const { Title } = Typography;

interface OwnerOption { value: number; label: string; }

const VehiclesPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('orders:write');

  const [items, setItems] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [form] = Form.useForm<VehiclePayload>();
  const [ownerType, setOwnerType] = useState<OwnerType | null>(null);

  // Info drawer (click on row)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
  // Secondary drawers for owner navigation
  const [cpDrawerOpen, setCpDrawerOpen] = useState(false);
  const [cpDrawerId, setCpDrawerId] = useState<number | null>(null);
  const [indDrawerOpen, setIndDrawerOpen] = useState(false);
  const [indDrawerId, setIndDrawerId] = useState<number | null>(null);

  const [individualOptions, setIndividualOptions] = useState<OwnerOption[]>([]);
  const [counterpartyOptions, setCounterpartyOptions] = useState<OwnerOption[]>([]);
  const [ownerLoading, setOwnerLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listVehicles({ page, per_page: perPage, search: search || undefined, include_inactive: includeInactive });
      setItems(data.items);
      setTotal(data.total);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить список');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const loadOwnerOptions = useCallback(async (type: OwnerType, query?: string) => {
    setOwnerLoading(true);
    try {
      if (type === 'individual') {
        const r = await api.get('/individuals', { params: { search: query, per_page: 50 } });
        const items = r.data.items || r.data || [];
        setIndividualOptions(items.map((i: any) => ({
          value: i.id,
          label: [i.last_name, i.first_name, i.middle_name].filter(Boolean).join(' '),
        })));
      } else {
        const r = await api.get('/counterparties', { params: { search: query, per_page: 50 } });
        const items = r.data.items || [];
        setCounterpartyOptions(items.map((c: any) => ({ value: c.id, label: c.short_name })));
      }
    } finally {
      setOwnerLoading(false);
    }
  }, []);

  const openCreate = () => {
    setEditing(null);
    setOwnerType(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalOpen(true);
  };

  const openEdit = (v: Vehicle) => {
    setEditing(v);
    setOwnerType(v.owner_type || null);
    form.setFieldsValue({
      make: v.make, model: v.model, year: v.year ?? null,
      license_plate: v.license_plate ?? null, vin: v.vin ?? null,
      estimated_value: v.estimated_value ?? null,
      owner_type: v.owner_type ?? null, owner_id: v.owner_id ?? null,
      notes: v.notes ?? null, is_active: v.is_active,
    });
    if (v.owner_type && v.owner_id) {
      // Prefill single option so Select shows label instead of id
      if (v.owner_type === 'individual') {
        setIndividualOptions([{ value: v.owner_id, label: v.owner_name || `#${v.owner_id}` }]);
      } else {
        setCounterpartyOptions([{ value: v.owner_id, label: v.owner_name || `#${v.owner_id}` }]);
      }
      loadOwnerOptions(v.owner_type);
    }
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: VehiclePayload = {
        ...values,
        year: values.year || null,
        estimated_value: values.estimated_value ?? null,
        owner_type: ownerType,
        owner_id: ownerType ? values.owner_id ?? null : null,
      };
      if (editing) {
        await updateVehicle(editing.id, payload);
        message.success('Автомобиль обновлён');
      } else {
        await createVehicle(payload);
        message.success('Автомобиль добавлен');
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    }
  };

  const handleArchive = async (v: Vehicle) => {
    try {
      await archiveVehicle(v.id);
      message.success('Автомобиль перенесён в архив');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  const columns = useMemo(() => [
    { title: 'Марка', dataIndex: 'make', key: 'make', width: 140 },
    { title: 'Модель', dataIndex: 'model', key: 'model', width: 160 },
    { title: 'Год', dataIndex: 'year', key: 'year', width: 80 },
    { title: 'Гос. номер', dataIndex: 'license_plate', key: 'license_plate', width: 140 },
    { title: 'VIN', dataIndex: 'vin', key: 'vin', width: 180, ellipsis: true },
    {
      title: 'Владелец', key: 'owner', width: 240,
      render: (_: unknown, v: Vehicle) => {
        if (!v.owner_type || !v.owner_id) return <span style={{ color: '#999' }}>—</span>;
        const label = v.owner_type === 'individual' ? 'Физлицо' : 'Юрлицо';
        return <span>{v.owner_name || `#${v.owner_id}`} <span style={{ color: '#999' }}>({label})</span></span>;
      },
    },
    {
      title: '', key: 'actions', width: 90, fixed: 'right' as const,
      onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
      render: (_: unknown, v: Vehicle) => (
        <Space size="small">
          {canWrite && <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(v)} />}
          {canWrite && v.is_active && (
            <Popconfirm title="Перенести в архив?" onConfirm={() => handleArchive(v)} okText="Да" cancelText="Нет">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ], [canWrite]);

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col><Title level={4} style={{ margin: 0 }}>Автомобили</Title></Col>
        <Col>
          <Space wrap>
            <Input.Search
              placeholder="Марка, модель, гос. номер, VIN"
              allowClear
              onSearch={(val) => { setPage(1); setSearch(val); }}
              style={{ width: 280 }}
            />
            <Space size={6}>
              <span>Показать архивные</span>
              <Switch checked={includeInactive} onChange={(v) => { setPage(1); setIncludeInactive(v); }} />
            </Space>
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Добавить</Button>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        onRow={(v) => ({
          onClick: () => { setDrawerId(v.id); setDrawerOpen(true); },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page, pageSize: perPage, total, showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
        scroll={{ x: 1000 }}
        rowClassName={(v) => (v.is_active ? '' : 'row-archived')}
      />

      <VehicleInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        vehicleId={drawerId}
        width={480}
        refreshKey={drawerRefreshKey}
        extra={canWrite && drawerId ? (
          <Space>
            <Button type="primary" onClick={() => {
              const v = items.find((x) => x.id === drawerId);
              if (v) { setDrawerOpen(false); openEdit(v); }
            }}>Редактировать</Button>
          </Space>
        ) : null}
        onOwnerClick={(ownerType, ownerId) => {
          setDrawerOpen(false);
          if (ownerType === 'individual') {
            setIndDrawerId(ownerId); setIndDrawerOpen(true);
          } else {
            setCpDrawerId(ownerId); setCpDrawerOpen(true);
          }
        }}
      />

      <IndividualInfoDrawer
        open={indDrawerOpen}
        onClose={() => setIndDrawerOpen(false)}
        individualId={indDrawerId}
        onCounterpartyClick={(cpId) => { setIndDrawerOpen(false); setCpDrawerId(cpId); setCpDrawerOpen(true); }}
      />

      <CounterpartyInfoDrawer
        open={cpDrawerOpen}
        onClose={() => setCpDrawerOpen(false)}
        counterpartyId={cpDrawerId}
        width={480}
        onContactClick={(id) => { setCpDrawerOpen(false); setIndDrawerId(id); setIndDrawerOpen(true); }}
      />

      <Modal
        title={editing ? 'Редактировать автомобиль' : 'Новый автомобиль'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Сохранить"
        cancelText="Отмена"
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="make" label="Марка" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <Input placeholder="Toyota" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="model" label="Модель" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <Input placeholder="Camry" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="year" label="Год">
                <InputNumber min={1900} max={2100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="license_plate" label="Гос. номер">
                <Input placeholder="А123БВ51" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="estimated_value" label="Оценочная стоимость, ₽">
                <InputNumber min={0} step={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="vin" label="VIN">
                <Input placeholder="17 символов" maxLength={17} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Владелец">
                <Radio.Group
                  value={ownerType ?? 'none'}
                  onChange={(e) => {
                    const v = e.target.value as OwnerType | 'none';
                    const newType = v === 'none' ? null : v;
                    setOwnerType(newType);
                    form.setFieldValue('owner_id', null);
                    if (newType) loadOwnerOptions(newType);
                  }}
                >
                  <Radio value="none">Без владельца</Radio>
                  <Radio value="individual">Физлицо</Radio>
                  <Radio value="counterparty">Юрлицо</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            {ownerType && (
              <Col span={24}>
                <Form.Item
                  name="owner_id"
                  label={ownerType === 'individual' ? 'Физлицо' : 'Контрагент'}
                  rules={[{ required: true, message: 'Выберите владельца' }]}
                >
                  <Select
                    showSearch
                    filterOption={false}
                    loading={ownerLoading}
                    onSearch={(q) => loadOwnerOptions(ownerType, q)}
                    options={ownerType === 'individual' ? individualOptions : counterpartyOptions}
                    placeholder="Начните вводить для поиска"
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item name="notes" label="Примечание">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
};

export default VehiclesPage;
